import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { WishlistProvider } from "./context/WishlistContext";
import { CartProvider } from "./context/CartContext";
import CartDrawer from "./components/CartDrawer";
import Header from "./components/Header";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import CategoryPage from "./pages/CategoryPage";
import ProductPage from "./pages/ProductPage";
import TryOnEntryPage from "./pages/TryOnEntryPage";
import TryOnExperimentPage from "./pages/TryOnExperimentPage";
import StyleQuizPage from "./pages/StyleQuizPage";
import NovidadesPage from "./pages/NovidadesPage";
import SobrePage from "./pages/SobrePage";
import WishlistPage from "./pages/WishlistPage";
import SearchPage from "./pages/SearchPage";
import PresentesPage from "./pages/PresentesPage";
import GiftCardPage from "./pages/GiftCardPage";
import GiftCardSuccessPage from "./pages/GiftCardSuccessPage";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import AuthCallback from "./pages/AuthCallback";
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import VerifyEmailPage from "./pages/auth/VerifyEmailPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminProductForm from "./pages/admin/AdminProductForm";
import AdminBulkImport from "./pages/admin/AdminBulkImport";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminCoupons from "./pages/admin/AdminCoupons";
import AdminGiftCards from "./pages/admin/AdminGiftCards";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminSales from "./pages/admin/AdminSales";

function PublicShell({ children }) {
  return (
    <>
      <Header />
      <main className="min-h-[60vh]">{children}</main>
      <Footer />
    </>
  );
}

function AppRoutes() {
  const location = useLocation();

  // Reset scroll position on every route change — prevents pages from
  // opening at the bottom when the previous route was a long page.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname]);

  // M248 — Apply admin-uploaded favicon at runtime. Runs ONCE per app mount
  // so every visitor (logged-in or not) gets the latest icon without a
  // redeploy. If no override exists yet (empty response), we leave the
  // static /public/favicon set in index.html untouched — that's our fallback.
  // Failures are silent: keeping the fallback is the safer behavior.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const apiBase = process.env.REACT_APP_BACKEND_URL;
        if (!apiBase) return;
        const r = await fetch(`${apiBase}/api/settings/favicon`);
        if (!r.ok) return;
        const data = await r.json();
        const path = (data?.url || "").replace(/^\/api\/files\//, "");
        if (cancelled || !path) return;
        const fullUrl = `${apiBase}/api/files/${path}`;
        document.querySelectorAll('link[rel*="icon"]').forEach((l) => {
          l.href = fullUrl;
        });
      } catch { /* keep static fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auth callback comes back as /auth/callback with #session_id=... in hash
  if (location.pathname === "/auth/callback") {
    return <AuthCallback />;
  }

  const isAdmin = location.pathname.startsWith("/admin");
  const isGiftSuccess = location.pathname === "/gift-card/sucesso";
  const isAuth = ["/login", "/cadastro", "/verificar-email", "/esqueci-senha", "/redefinir-senha"].includes(location.pathname);

  if (isAdmin) {
    return (
      <Routes>
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminLogin />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="produtos" element={<AdminProducts />} />
          <Route path="produtos/importar" element={<AdminBulkImport />} />
          <Route path="produtos/:id" element={<AdminProductForm />} />
          <Route path="configuracoes" element={<AdminSettings />} />
          <Route path="cupons" element={<AdminCoupons />} />
          <Route path="gift-cards" element={<AdminGiftCards />} />
          <Route path="clientes" element={<AdminCustomers />} />
          <Route path="vendas" element={<AdminSales />} />
        </Route>
      </Routes>
    );
  }

  if (isAuth) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/cadastro" element={<RegisterPage />} />
        <Route path="/verificar-email" element={<VerifyEmailPage />} />
        <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
        <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
      </Routes>
    );
  }

  if (isGiftSuccess) {
    return <Routes><Route path="/gift-card/sucesso" element={<GiftCardSuccessPage />} /></Routes>;
  }

  return (
    <PublicShell>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/joias/:slug" element={<CategoryPage />} />
        <Route path="/produto/:id" element={<ProductPage />} />
        <Route path="/try-on" element={<TryOnEntryPage />} />
        <Route path="/try-on/experimentar" element={<TryOnExperimentPage />} />
        <Route path="/try-on/quiz" element={<StyleQuizPage />} />
        <Route path="/novidades" element={<NovidadesPage />} />
        <Route path="/sobre" element={<SobrePage />} />
        <Route path="/wishlist" element={<WishlistPage />} />
        <Route path="/busca" element={<SearchPage />} />
        <Route path="/presentes" element={<PresentesPage />} />
        <Route path="/gift-card" element={<GiftCardPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/checkout/sucesso" element={<CheckoutSuccessPage />} />
        <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
      </Routes>
    </PublicShell>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <WishlistProvider>
            <CartProvider>
              <AppRoutes />
              <CartDrawer />
              <Toaster position="bottom-right" richColors />
            </CartProvider>
          </WishlistProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
